import { Injectable, inject } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable } from 'rxjs';
import { tap, finalize } from 'rxjs/operators';

import { HttpMethodEnum } from '../../models/enums/http-method.enum';
import { ErrorMessagesService } from '../error-messages.service';
import { ErrorMessageModel } from '../../core/models/error-message.model';
import { ErrorMessageTypeEnum } from '../../core/models/enums/error-message-type.enum';
import { AlertService } from '../alert.service';
import { SecurityStorage } from '../storages/security.storage';
import { FailedRequestsRepository } from '../repositories/failed-requests.repository';
import { ErrorMessageMapper, BUSINESS_EXCEPTION_STATUS } from '../error-message-mapper.service';
import { LoggerService } from '../logger.service';

@Injectable()
export class ErrorHandlerHttpInterceptor implements HttpInterceptor {
    private router = inject(Router);
    private failedRequestsRepository = inject(FailedRequestsRepository);
    private errorMessagesService = inject(ErrorMessagesService);
    private alertService = inject(AlertService);
    private securityStorage = inject(SecurityStorage);
    private mapper = inject(ErrorMessageMapper);
    private logger = inject(LoggerService);

    private pendingTransactions = 0;

    intercept(
        request: HttpRequest<any>,
        next: HttpHandler
    ): Observable<HttpEvent<any>> {
        let serverError: any;
        let hasError = false;

        if (this.pendingTransactions === 0) {
            this.errorMessagesService.setError(undefined);
        }

        this.pendingTransactions++;

        return next.handle(request).pipe(
            tap(
                event => {},
                error => {
                    hasError = true;
                    serverError = error;
                }
            ),
            finalize(() => {
                if (hasError && serverError instanceof HttpErrorResponse) {
                    // The raw error object may contain PHI, stack traces, SQL
                    // fragments or internal IDs. It goes ONLY to the structured
                    // logger (dev console / prod sink) and is never surfaced to
                    // the user (Issue #8).
                    this.logger.error(
                        'HTTP request failed',
                        serverError,
                        request.method,
                        request.url
                    );

                    switch (serverError.status) {
                        case 0: // Handle offline mode
                            const req = request.clone();

                            if (
                                req.method === HttpMethodEnum.Post ||
                                req.method === HttpMethodEnum.Put ||
                                req.method === HttpMethodEnum.Delete
                            ) {
                                this.failedRequestsRepository.saveRequest(
                                    req.urlWithParams,
                                    req.method,
                                    req.body
                                );
                            }
                            break;
                        case 401: // Handle user not authenticated or forbidden
                        case 403:
                            // Last line of defence (Issue #6): the AuthHttpInterceptor
                            // runs closer to the backend (registered after this one in
                            // interceptors/index.ts) and gets the first chance to
                            // silently refresh the token and retry. A 401 only reaches
                            // here when that refresh was impossible or failed, so the
                            // existing hard logout/redirect is the correct fallback.
                            this.securityStorage.eraseUserInfo();
                            this.router.navigate(['login']);
                            break;
                        case BUSINESS_EXCEPTION_STATUS: // 588: business exceptions
                            // Author-intended, user-facing backend message
                            // (sanitized: trimmed + length-capped by the mapper).
                            this.showSanitizedError(serverError, true);
                            break;
                        default:
                            // 400, 404, 408, 409, 422, 500, 503 and any other
                            // 4xx/5xx: show a generic, status-based message. The
                            // raw server message is never displayed.
                            this.showSanitizedError(serverError, false);
                            break;
                    }
                }
            })
        );
    }

    private showSanitizedError(
        serverError: HttpErrorResponse,
        isBusinessException: boolean
    ): void {
        const userMessage = this.mapper.toUserMessage(serverError);

        this.errorMessagesService.setError(
            new ErrorMessageModel({
                message: userMessage,
                type: ErrorMessageTypeEnum.Warning
            })
        );

        if (!this.errorMessagesService.shouldDisplayAlertOnError) {
            return;
        }

        if (isBusinessException) {
            this.alertService.showAlert(userMessage, undefined);
        } else {
            this.alertService.showError(userMessage, undefined);
        }
    }
}
